// HouseholdAssetAnalysis.js
import React, { useState, useEffect, useMemo } from "react";
import api from "../../../utils/apiConfig";
import { formatCurrency } from "../../../utils/formatters";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { useTheme } from "../../../contexts/ThemeContext";
import EditHouseholdAssetsModal from "./EditHouseholdAssetsModal";
import logger from "../../../utils/logger";

const TABS = [
  { key: "cash", label: "Cash & Eq." },
  { key: "property", label: "Property" },
  { key: "investments", label: "Investments" },
  { key: "all", label: "All" },
];

const Tooltip = ({ label, children, side = "top" }) => (
  <div className="relative group inline-flex">
    {children}
    <span
      className={`
        pointer-events-none absolute whitespace-nowrap rounded-md bg-gray-900 dark:bg-gray-700
        text-white text-[10px] px-2 py-1 shadow-md opacity-0 group-hover:opacity-100
        transition-opacity duration-150
        ${side === "top" ? "-top-7 left-1/2 -translate-x-1/2" : "top-full mt-1 left-1/2 -translate-x-1/2"}
      `}
    >
      {label}
    </span>
  </div>
);

// Inline Stat component for summary/statistics display
const Stat = ({ label, value, icon, isDarkMode }) => (
  <div
    className={`flex-1 min-w-0 flex flex-col items-center justify-center p-0.5 rounded-lg ${isDarkMode
        ? "bg-gray-800 hover:bg-gray-700"
        : "bg-surface-default dark:bg-surface-default hover:bg-gray-50 dark:hover:bg-gray-800"
      } transition-colors duration-200`}
  >
    {icon && (
      <div className="w-3 h-3 flex justify-center mb-0.5">
        <span className="text-sm">{icon}</span>
      </div>
    )}
    {typeof label === "string" ? (
      <span
        className={`text-[10px] ${isDarkMode ? "text-gray-400" : "text-gray-500 dark:text-gray-500"
          }`}
      >
        {label}
      </span>
    ) : (
      label
    )}
    {typeof value === "string" || typeof value === "number" ? (
      <span
        className={`text-xs font-semibold ${isDarkMode ? "text-white" : "text-gray-900 dark:text-gray-100"
          }`}
      >
        {value}
      </span>
    ) : (
      value
    )}
  </div>
);

const HouseholdAssetAnalysis = ({
  assetsData = {
    assets: [],
    assetTypes: [],
    taxQualifications: [],
    accountTypes: [],
    financialInstitutions: [],
    properties: [],
    members: [],
    ownerTypes: [],
  },
  primaryResidenceValueData = 0,
  ownerTypes = [],
  onSave,
}) => {
  const { isDarkMode } = useTheme();

  logger.info("HouseholdAssetAnalysis received data:", {
    assets: assetsData.assets?.map((a) => ({
      id: a.asset_id,
      userId: a.user_id,
      ownerTypeId: a.owner_type_id,
      member: a.member,
    })),
    members: assetsData.members?.map((m) => ({
      userId: m.user_id || m.userId,
      firstName: m.firstName,
      lastName: m.lastName,
      isFormSubmitter: m.isFormSubmitter,
    })),
    ownerTypes: ownerTypes?.map((ot) => ({
      id: ot.owner_type_id,
      name: ot.owner_type_name,
    })),
  });

  // State declarations
  const [primaryResidenceValue, setPrimaryResidenceValue] = useState(
    parseFloat(
      (
        assetsData.properties?.[0]?.residence_value ||
        primaryResidenceValueData ||
        0
      )
        .toString()
        .replace(/[$,]/g, "")
    )
  );
  const [localAssetTypes, setLocalAssetTypes] = useState([]);
  const [localTaxQualifications, setLocalTaxQualifications] = useState([]);
  const [localAccountTypes, setLocalAccountTypes] = useState([]);
  const [localFinancialInstitutions, setLocalFinancialInstitutions] =
    useState([]);
  const [localOwnerTypes, setLocalOwnerTypes] = useState([]);
  const [error, setError] = useState(null);
  const [view, setView] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Use either prop reference data or locally fetched ones
  const assetTypesToUse =
    assetsData.assetTypes?.length > 0 ? assetsData.assetTypes : localAssetTypes;
  const taxQualificationsToUse =
    assetsData.taxQualifications?.length > 0
      ? assetsData.taxQualifications
      : localTaxQualifications;
  const accountTypesToUse =
    assetsData.accountTypes?.length > 0
      ? assetsData.accountTypes
      : localAccountTypes;
  const financialInstitutionsToUse =
    assetsData.financialInstitutions?.length > 0
      ? assetsData.financialInstitutions
      : localFinancialInstitutions;
  const ownerTypesToUse =
    ownerTypes?.length > 0
      ? ownerTypes
      : assetsData.ownerTypes?.length > 0
        ? assetsData.ownerTypes
        : localOwnerTypes;

  // Debug log for owner types
  logger.info(
    "Owner types being used:",
    ownerTypesToUse.map((ot) => ({
      id: ot.owner_type_id,
      name: ot.owner_type_name,
    }))
  );

  // Find a property type asset ID or create one
  const findPropertyAssetTypeId = () => {
    const propertyType = assetTypesToUse.find(
      (type) =>
        type.asset_type_name?.toLowerCase().includes("real estate") ||
        type.asset_type_name?.toLowerCase().includes("property") ||
        type.asset_type_name?.toLowerCase().includes("residence")
    );

    if (propertyType) {
      return propertyType.asset_type_id.toString();
    }

    const homeType = assetTypesToUse.find(
      (type) =>
        type.asset_type_name?.toLowerCase().includes("home") ||
        type.asset_type_name?.toLowerCase().includes("house")
    );

    if (homeType) {
      return homeType.asset_type_id.toString();
    }

    return assetTypesToUse.length > 0
      ? assetTypesToUse[0].asset_type_id.toString()
      : "1";
  };

  // Fetchers (only if not provided)
  useEffect(() => {
    const fetchAssetTypes = async () => {
      if (
        (!assetsData.assetTypes || assetsData.assetTypes.length === 0) &&
        localAssetTypes.length === 0
      ) {
        try {
          const response = await api.get("/advisor-portal/asset-types");
          if (response.data && Array.isArray(response.data)) {
            setLocalAssetTypes(response.data);
          }
        } catch (error) {
          logger.error("Error fetching asset types:", error);
        }
      }
    };
    fetchAssetTypes();
  }, [assetsData.assetTypes, localAssetTypes.length]);

  useEffect(() => {
    const fetchTaxQualifications = async () => {
      if (
        (!assetsData.taxQualifications ||
          assetsData.taxQualifications.length === 0) &&
        localTaxQualifications.length === 0
      ) {
        try {
          const response = await api.get("/advisor-portal/tax-qualifications");
          if (response.data && Array.isArray(response.data)) {
            setLocalTaxQualifications(response.data);
          }
        } catch (error) {
          logger.error("Error fetching tax qualifications:", error);
        }
      }
    };
    fetchTaxQualifications();
  }, [assetsData.taxQualifications, localTaxQualifications.length]);

  useEffect(() => {
    const fetchAccountTypes = async () => {
      if (
        (!assetsData.accountTypes || assetsData.accountTypes.length === 0) &&
        localAccountTypes.length === 0
      ) {
        try {
          const response = await api.get("/advisor-portal/account-types");
          if (response.data && Array.isArray(response.data)) {
            setLocalAccountTypes(response.data);
          }
        } catch (error) {
          logger.error("Error fetching account types:", error);
        }
      }
    };
    fetchAccountTypes();
  }, [assetsData.accountTypes, localAccountTypes.length]);

  useEffect(() => {
    const fetchFinancialInstitutions = async () => {
      if (
        (!assetsData.financialInstitutions ||
          assetsData.financialInstitutions.length === 0) &&
        localFinancialInstitutions.length === 0
      ) {
        try {
          const response = await api.get(
            "/advisor-portal/financial-institutions"
          );
          if (response.data && Array.isArray(response.data)) {
            setLocalFinancialInstitutions(response.data);
          }
        } catch (error) {
          logger.error("Error fetching financial institutions:", error);
        }
      }
    };
    fetchFinancialInstitutions();
  }, [
    assetsData.financialInstitutions,
    localFinancialInstitutions.length,
  ]);

  useEffect(() => {
    const fetchOwnerTypes = async () => {
      if (localOwnerTypes.length === 0) {
        try {
          const response = await api.get("/advisor-portal/owner-types");
          if (response.data && Array.isArray(response.data)) {
            setLocalOwnerTypes(response.data);
          }
        } catch (error) {
          logger.error("Error fetching owner types:", error);
        }
      }
    };
    fetchOwnerTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Transform raw assets into normalized list
  const processedAssets = () => {
    const formId =
      assetsData.assets?.[0]?.form_id ||
      assetsData.properties?.[0]?.form_id ||
      null;

    const propertyUpdatedAt =
      assetsData.properties?.[0]?.updated_at || new Date().toISOString();
    const propertyCreatedAt =
      assetsData.properties?.[0]?.created_at || new Date().toISOString();

    // Map owner_type_id -> member user_id
    let ownerTypeToMemberId = {};
    if (assetsData.members && assetsData.members.length > 0) {
      const primaryMember = assetsData.members.find(
        (m) => m.isFormSubmitter === true
      );
      const spouseMember = assetsData.members.find(
        (m) => m.isFormSubmitter !== true
      );

      if (primaryMember) {
        const selfType = ownerTypesToUse.find(
          (ot) => ot.owner_type_name === "Self"
        );
        if (selfType) {
          ownerTypeToMemberId[selfType.owner_type_id] = primaryMember.userId;
        }
      }

      if (spouseMember) {
        const spouseType = ownerTypesToUse.find(
          (ot) => ot.owner_type_name === "Spouse"
        );
        if (spouseType) {
          ownerTypeToMemberId[spouseType.owner_type_id] = spouseMember.userId;
        }
      }
    }

    // Primary residence pseudo-asset
    const primaryResidenceAsset = {
      id: "primary-residence",
      isPrimaryResidence: true,
      memberId: null,
      member: "Joint",
      financialInstitution: "Primary Residence",
      financialInstitutionId: null,
      assetType: findPropertyAssetTypeId(),
      assetTypeName: "Primary Residence",
      taxQualification: "2",
      taxQualificationName: "Non-Qualified",
      accountType: "1",
      accountTypeName: "Property",
      ownerTypeId: "",
      formId: formId,
      balance: 555000,  // HARDCODED BECAUSE I'M A USELESS PIECE OF SHIT
      riskTolerance: "",
      lastUpdated: new Date(propertyUpdatedAt).toISOString().split("T")[0],
      updated_at: propertyUpdatedAt,
      created_at: propertyCreatedAt,
      deleted: false,
    };

    const regularAssets = (assetsData.assets || []).map((asset) => {
      const assetTypeObj = assetTypesToUse.find(
        (type) =>
          type.asset_type_id?.toString() === asset.asset_type_id?.toString()
      );
      const assetTypeName = assetTypeObj
        ? assetTypeObj.asset_type_name
        : "Unknown";

      const mappedUserId =
        asset.owner_type_id && ownerTypeToMemberId[asset.owner_type_id]
          ? ownerTypeToMemberId[asset.owner_type_id]
          : asset.user_id;

      let memberName = "Unknown";

      if (
        asset.ownership_structure_id === 2 ||
        (asset.asset_type_id === 13 && asset.secondary_owner_user_id) ||
        (asset.asset_type_id === 20 && asset.secondary_owner_user_id)
      ) {
        memberName = "Joint";
      } else if (mappedUserId) {
        const member = assetsData.members.find(
          (m) =>
            m.user_id?.toString() === mappedUserId?.toString() ||
            m.userId?.toString() === mappedUserId?.toString()
        );
        if (member) {
          memberName = `${member.firstName || ""} ${member.lastName || ""
            }`.trim();
        }
      } else if (asset.owner_type_id) {
        const ownerType = ownerTypesToUse.find(
          (ot) =>
            ot.owner_type_id?.toString() === asset.owner_type_id?.toString()
        );
        if (ownerType) {
          const ownerTypeName = ownerType.owner_type_name.toLowerCase();
          if (assetsData.members.length === 1) {
            const primaryMember = assetsData.members[0];
            if (primaryMember) {
              memberName = `${primaryMember.firstName || ""} ${primaryMember.lastName || ""
                }`.trim();
            }
          }

          if (assetsData.members.length > 1) {
            if (ownerTypeName === "joint" || ownerTypeName === "household") {
              memberName = "Joint";
            } else if (ownerTypeName === "self") {
              const primaryMember = assetsData.members.find(
                (m) => m.isFormSubmitter
              );
              if (primaryMember) {
                memberName = `${primaryMember.firstName || ""} ${primaryMember.lastName || ""
                  }`.trim();
              }
            } else if (ownerTypeName === "spouse") {
              const spouseMember = assetsData.members.find(
                (m) => !m.isFormSubmitter
              );
              if (spouseMember) {
                memberName = `${spouseMember.firstName || ""} ${spouseMember.lastName || ""
                  }`.trim();
              }
            } else {
              memberName = ownerType.owner_type_name;
            }
          }
        }
      }

      const lastUpdated = asset.updated_at
        ? new Date(asset.updated_at).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];

      return {
        id:
          asset.asset_id ||
          asset.id ||
          `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        user_id: mappedUserId,
        memberId: mappedUserId,
        member: memberName,
        financialInstitution: asset.financialInstitution || "",
        financialInstitutionId: asset.financial_institution_id || null,
        assetType: asset.asset_type_id?.toString() || "",
        assetTypeName,
        taxQualification: asset.tax_qualification_id?.toString() || "",
        accountType: asset.account_type_id?.toString() || "",
        ownerTypeId: asset.owner_type_id?.toString() || "",
        formId: asset.form_id || null,
        balance: asset.approximate_value || asset.checking_savings_balance || 0,
        riskTolerance: asset.overall_portfolio_risk_number || "",
        lastUpdated,
        updated_at: asset.updated_at,
        created_at: asset.created_at,
        deleted: false,
        ownership_structure_id: asset.ownership_structure_id,
        secondary_owner_user_id: asset.secondary_owner_user_id,
      };
    });

    return [primaryResidenceAsset, ...regularAssets];
  };

  const allAssets = useMemo(
    () => processedAssets().filter((a) => !a.deleted),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assetsData, primaryResidenceValue]
  );

  // Tab filters
  const byCash = useMemo(() => {
    const list = allAssets.filter((a) => {
      const n = a.assetTypeName?.toLowerCase() || "";
      return (
        n.includes("cash") ||
        n.includes("checking") ||
        n.includes("savings") ||
        n.includes("money market") ||
        n.includes("cd") ||
        n.includes("bank account") ||
        n.includes("deposit") ||
        n.includes("liquid")
      );
    });
    return list;
  }, [allAssets]);

  const byProp = useMemo(
    () =>
      allAssets.filter(
        (a) =>
          a.isPrimaryResidence ||
          a.assetTypeName?.toLowerCase().includes("property")
      ),
    [allAssets]
  );

  const byInv = useMemo(
    () => allAssets.filter((a) => !byCash.includes(a) && !byProp.includes(a)),
    [allAssets, byCash, byProp]
  );

  const displayed =
    view === "all"
      ? allAssets
      : view === "cash"
        ? byCash
        : view === "property"
          ? byProp
          : byInv;

  // Summary calcs
  const { traditionalTotal, rothTotal, nonQualifiedTotal } = useMemo(() => {
    let traditional = 0,
      roth = 0,
      nonQ = 0;
    allAssets.forEach((a) => {
      const n = a.taxQualification?.toString() || "";
      if (n === "1" || /trad/i.test(a.assetTypeName))
        traditional += parseFloat(a.balance) || 0;
      else if (n === "2" || /roth|tax[- ]?free/i.test(a.assetTypeName))
        roth += parseFloat(a.balance) || 0;
      else nonQ += parseFloat(a.balance) || 0;
    });
    return {
      traditionalTotal: traditional,
      rothTotal: roth,
      nonQualifiedTotal: nonQ,
    };
  }, [allAssets]);

  // Chart time series (last point = current total)
  const timeSeries = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        month: d.toLocaleString("default", { month: "short", year: "2-digit" }),
        value: 0,
      });
    }
    if (displayed.length) {
      months[months.length - 1].value = displayed.reduce(
        (sum, a) => sum + (parseFloat(a.balance) || 0),
        0
      );
    }
    return months;
  }, [displayed]);

  const totalValue = useMemo(
    () => displayed.reduce((sum, a) => sum + (parseFloat(a.balance) || 0), 0),
    [displayed]
  );

  const avgRisk = useMemo(() => {
    const risks = displayed
      .map((a) => parseFloat(a.riskTolerance))
      .filter((x) => !isNaN(x));
    return risks.length
      ? Math.round(risks.reduce((a, b) => a + b, 0) / risks.length)
      : 0;
  }, [displayed]);

  // Save handler from modal
  const handleSaveAssets = (updatedAssets) => {
    const primaryResidenceAsset = updatedAssets.find(
      (asset) => asset.isPrimaryResidence
    );

    if (primaryResidenceAsset) {
      const newValue = parseFloat(primaryResidenceAsset.balance) || 0;
      setPrimaryResidenceValue(newValue);
    }

    const regularAssets = updatedAssets.filter(
      (asset) => !asset.isPrimaryResidence
    );

    if (onSave) {
      const preparedAssets = regularAssets.map((asset) => ({
        asset_id:
          asset.id && !asset.id.toString().startsWith("temp-")
            ? asset.id
            : undefined,
        id: asset.id,
        user_id: asset.memberId,
        financial_institution_id: asset.financialInstitutionId,
        asset_type_id: parseInt(asset.assetType) || null,
        tax_qualification_id: parseInt(asset.taxQualification) || null,
        account_type: parseInt(asset.accountType) || null,
        owner_type_id: parseInt(asset.ownerTypeId) || null,
        form_id: asset.formId,
        approximate_value: parseFloat(asset.balance) || 0,
        overall_portfolio_risk_number: asset.riskTolerance,
      }));

      onSave({
        assets: preparedAssets,
        primaryResidenceValue: primaryResidenceAsset?.balance || 0,
        properties: assetsData.properties,
      });
    }
  };

  useEffect(() => {
    logger.info("Assets Card allAssets:", allAssets);
    logger.info(
      "Assets Card calculatedTotalAssets:",
      allAssets.reduce((sum, a) => sum + (parseFloat(a.balance) || 0), 0)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div 
      className="p-4 space-y-4 relative group cursor-pointer transition-all"
      onClick={() => setIsModalOpen(true)}
    >
      {/* Top-right tooltip */}
      <div
        className={`absolute -top-2 -right-2 rounded-lg px-3 py-1.5 text-xs font-medium
                   bg-green-600 text-white opacity-0 group-hover:opacity-100
                   transform translate-y-1 group-hover:translate-y-0
                   transition-all z-50`}
      >
        View/Edit Asset Details
        <div className="absolute -bottom-1 right-3 w-2 h-2 bg-green-600 transform rotate-45" />
      </div>

      {error && (
        <div
          className={`mb-1 p-1 ${isDarkMode
              ? "bg-red-900/30 border-red-700 text-red-200"
              : "bg-red-100 border-red-400 text-red-700"
            } rounded border text-[10px]`}
        >
          {error}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2
          className={`text-sm font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-700"
            }`}
        >
          Assets
        </h2>
      </div>

      {/* Tabs for summary view */}
      <div className="flex gap-0.5 mb-1">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`px-1.5 py-0.5 rounded-full border text-[10px] font-medium transition-colors ${view === tab.key
                ? isDarkMode
                  ? "bg-blue-800 text-white border-blue-700"
                  : "bg-blue-600 text-white border-blue-600"
                : isDarkMode
                  ? "bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700"
                  : "bg-gray-100 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-blue-50"
              }`}
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering parent onClick
              setView(tab.key);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Sparkline / small AreaChart */}
      <div
        className={`w-full mx-auto h-24 mb-1`}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={timeSeries} margin={{ top: 2, right: 15, left: 30, bottom: 0 }}>
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={isDarkMode ? "#60A5FA" : "#3B82F6"}
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor={isDarkMode ? "#60A5FA" : "#3B82F6"}
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="month"
              tick={{ fontSize: 6, fill: isDarkMode ? "#9CA3AF" : "#4B5563" }}
              stroke={isDarkMode ? "#4B5563" : "#E5E7EB"}
            />
            <YAxis
              tickFormatter={formatCurrency}
              width={40}
              tick={{ fontSize: 6, fill: isDarkMode ? "#9CA3AF" : "#4B5563" }}
              stroke={isDarkMode ? "#4B5563" : "#E5E7EB"}
            />
            <CartesianGrid strokeDasharray="2 2" stroke={isDarkMode ? "#374151" : "#E5E7EB"} />
            <RechartsTooltip
              formatter={formatCurrency}
              contentStyle={{
                backgroundColor: isDarkMode ? "#1F2937" : "#FFFFFF",
                border: `1px solid ${isDarkMode ? "#374151" : "#E5E7EB"}`,
                color: isDarkMode ? "#F3F4F6" : "#111827",
                fontSize: "8px",
              }}
              labelStyle={{
                color: isDarkMode ? "#9CA3AF" : "#4B5563",
                fontSize: "8px",
              }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={isDarkMode ? "#60A5FA" : "#3B82F6"}
              fillOpacity={1}
              fill="url(#colorValue)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Stats row */}
      <div className="w-full mx-auto flex justify-between gap-x-0.5 mt-1 items-center">
        <Stat icon="📊" label="Accounts" value={displayed.length} isDarkMode={isDarkMode} />
        <Stat
          icon="💵"
          label="Cash & Eq."
          value={formatCurrency(
            byCash.reduce((sum, a) => sum + (parseFloat(a.balance) || 0), 0)
          )}
          isDarkMode={isDarkMode}
        />
        <Stat
          icon="🏠"
          label="Property"
          value={formatCurrency(
            byProp.reduce((sum, a) => sum + (parseFloat(a.balance) || 0), 0)
          )}
          isDarkMode={isDarkMode}
        />
        <Stat
          icon="📈"
          label="Investments"
          value={formatCurrency(
            byInv.reduce((sum, a) => sum + (parseFloat(a.balance) || 0), 0)
          )}
          isDarkMode={isDarkMode}
        />
      </div>

      {/* Total row */}
      <div className="mt-1 flex justify-end">
        <span
          className={`font-semibold text-xs ${isDarkMode ? "text-white" : "text-gray-900 dark:text-gray-100"}`}
        >
          Total Assets:{" "}
          {formatCurrency(
            allAssets.reduce((sum, a) => sum + (parseFloat(a.balance) || 0), 0)
          )}
        </span>
      </div>

      {/* Details/Edit Modal */}
      <EditHouseholdAssetsModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveAssets}
        assetData={allAssets}
        referenceData={{
          assetTypes: assetTypesToUse,
          taxQualifications: taxQualificationsToUse,
          accountTypes: accountTypesToUse,
          financialInstitutions: financialInstitutionsToUse,
          ownerTypes: ownerTypesToUse,
          members: assetsData.members,
        }}
        primaryResidenceValue={primaryResidenceValue}
        title="Household Asset Analysis"
      />
    </div>
  );
};

export default HouseholdAssetAnalysis;
